import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RENT_FREQUENCIES,
  formatAddress,
  formatNzDate,
  formatRef,
  parseAmount,
  streetLine,
  type Money,
} from '@promanage/core';
import { Button, Chip, Field, Input, Panel, PanelHeader, Select, Textarea } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { useUnsavedChanges } from '../components/UnsavedChanges';
import { invalidateAccounts } from '../features/ledger/invalidate';
import { perLabel } from '../features/property/rent';
import { todayIso } from '../features/property/today';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/tenancies/new')({
  component: NewTenancyPage,
});

/**
 * Start a tenancy.
 *
 * THE SAME SHAPE AS `/tenancies/TE000004`, deliberately: identity card, then rent leading the
 * main column with the letting's own facts down the side. Every panel is where it will be after
 * saving, so filling this in is a rehearsal of the record rather than a different screen that
 * happens to produce one.
 *
 * Two panels on that page have no equivalent here and are not faked. "People" needs person
 * records and roles, which attach afterwards from the tenancy — a person is one record however
 * many tenancies they are part of, and inventing them from a tenancy form would create a second
 * one. The heading is the address for the same reason: a tenancy is named after its tenants, and
 * it has none yet.
 *
 * VACANT PROPERTIES FIRST in the picker, and the let ones are marked. A property can only be let
 * to one tenancy at a time and the database enforces it, so a picker that presented all
 * properties equally would be inviting a refusal — but the let ones stay selectable, because a
 * tenancy starting the day after the current one ends is legitimate and common.
 */
function NewTenancyPage() {
  const navigate = useNavigate();
  const unsaved = useUnsavedChanges();
  const queryClient = useQueryClient();
  const today = todayIso();

  const properties = useQuery(trpc.property.list.queryOptions(undefined, { staleTime: 30_000 }));

  const [propertyId, setPropertyId] = useState('');
  const [termType, setTermType] = useState<'fixed' | 'periodic'>('periodic');
  const [startsOn, setStartsOn] = useState(today);
  const [fixedTermEndsOn, setFixedTermEndsOn] = useState('');
  const [rentAmount, setRentAmount] = useState('');
  const [frequency, setFrequency] = useState<(typeof RENT_FREQUENCIES)[number]>('weekly');
  const [notes, setNotes] = useState('');

  const create = useMutation(
    trpc.property.createTenancy.mutationOptions({
      meta: { errorTitle: 'Could not start that tenancy' },
      onSuccess(created) {
        unsaved.saved();
        void queryClient.invalidateQueries({ queryKey: trpc.property.tenancies.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.property.list.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.property.get.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.rent.charges.queryKey() });
        // A tenancy opens a ledger account, so the ledger's own views changed too.
        invalidateAccounts(queryClient);

        // Onto the tenancy itself. Attaching the tenants is the next thing anybody does, and
        // that is the page it is done on — the same page this form was a rehearsal of.
        void navigate({
          to: '/tenancies/$ref',
          params: { ref: formatRef('tenancy', created.refSeq) },
        });
      },
    }),
  );

  const all = properties.data ?? [];
  const vacant = all.filter((p) => p.currentTenancy === null);
  const let_ = all.filter((p) => p.currentTenancy !== null);
  const chosen = all.find((p) => p.id === propertyId) ?? null;

  // Empty is "no rent agreed yet", which is a real state — a tenancy with no schedule simply
  // raises no charges. Anything typed has to parse into cents before it can be sent.
  let rentCents: Money | null = null;
  try {
    rentCents = rentAmount.trim() === '' ? null : parseAmount(rentAmount);
  } catch {
    rentCents = null;
  }
  const rentIssue =
    rentAmount.trim() === ''
      ? null
      : rentCents === null
        ? 'That is not an amount'
        : rentCents <= 0n
          ? 'Rent has to be above zero'
          : null;

  const ready =
    propertyId !== '' &&
    startsOn !== '' &&
    (termType !== 'fixed' || fixedTermEndsOn !== '') &&
    rentIssue === null;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!ready) return;

    create.mutate({
      propertyId,
      termType,
      startsOn,
      ...(termType === 'fixed' ? { fixedTermEndsOn } : {}),
      ...(rentCents === null ? {} : { rent: { amountCents: rentCents.toString(), frequency } }),
      ...(notes.trim() === '' ? {} : { notes: notes.trim() }),
    });
  };

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="New tenancy"
        subtitle="A letting of one property, from a date"
        actions={
          <Link to="/tenancies">
            <Button size="sm" variant="secondary">
              All tenancies
            </Button>
          </Link>
        }
      />

      <form onChange={unsaved.touch} onSubmit={submit} noValidate className="flex flex-col gap-4">
        {/* ── Identity ───────────────────────────────────────────────────────── */}
        {/*
         * Which property is the letting's identity, so the picker sits where the heading sits
         * on the record — and the address underneath fills in on choosing, which is the check
         * that the right one of two similar addresses was picked.
         */}
        <Panel className="flex flex-wrap items-end justify-between gap-4 p-5">
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-[1.375rem] font-extrabold leading-tight tracking-[-0.02em] text-ink">
              {chosen ? streetLine(chosen) : <span className="text-ink-subtle">New tenancy</span>}
            </h2>

            <div className="mt-3 max-w-lg">
              <Field
                label="Property"
                required
                hint="Vacant ones first — a property is let to one tenancy at a time"
              >
                {(props) => (
                  <Select
                    {...props}
                    value={propertyId}
                    onChange={(e) => setPropertyId(e.target.value)}
                  >
                    <option value="">Choose a property…</option>
                    {vacant.length > 0 ? (
                      <optgroup label="Vacant">
                        {vacant.map((property) => (
                          <option key={property.id} value={property.id}>
                            {streetLine(property)} · {property.city}
                          </option>
                        ))}
                      </optgroup>
                    ) : null}
                    {let_.length > 0 ? (
                      <optgroup label="Currently let">
                        {let_.map((property) => (
                          <option key={property.id} value={property.id}>
                            {streetLine(property)} · {property.city} ·{' '}
                            {formatRef('tenancy', property.currentTenancy?.refSeq ?? 0)}
                          </option>
                        ))}
                      </optgroup>
                    ) : null}
                  </Select>
                )}
              </Field>
            </div>

            {chosen ? (
              <p className="mt-2 text-[0.8125rem] text-ink-muted">{formatAddress(chosen)}</p>
            ) : null}
          </div>

          <div className="flex flex-col items-start gap-1 sm:items-end">
            <Chip tone={startsOn > today ? 'accent' : 'positive'}>
              {startsOn > today ? 'Upcoming' : 'Active'}
            </Chip>
            <span className="text-[0.75rem] text-ink-subtle tabular-nums">
              {startsOn === '' ? '—' : formatNzDate(startsOn)} –{' '}
              {termType === 'fixed' && fixedTermEndsOn !== ''
                ? formatNzDate(fixedTermEndsOn)
                : 'present'}
            </span>
            {chosen?.currentTenancy ? (
              /*
               * Selectable, but said out loud. A tenancy starting the day after the current one
               * ends is normal; one that overlaps is refused by the database, and being told
               * now beats being told on submit.
               */
              <span className="text-[0.6875rem] text-warn">
                Currently let — this one must start after it ends
              </span>
            ) : null}
          </div>
        </Panel>

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
            {/* ── Rent ─────────────────────────────────────────────────────── */}
            {/*
             * Rent leads, as it does on the record. Set here because the signed agreement in
             * hand is where the figure comes from — it applies from the tenancy's own start
             * date, and every later change is time-sliced on the tenancy page.
             */}
            <Panel className="p-5">
              <PanelHeader
                title="Rent"
                subtitle="From the day the tenancy starts"
                className="p-0 pb-3"
              />

              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Amount" hint="Per cycle — not per week unless the cycle is a week">
                  {(props) => (
                    <Input
                      {...props}
                      inputMode="decimal"
                      placeholder="0.00"
                      value={rentAmount}
                      onChange={(e) => setRentAmount(e.target.value)}
                      className="text-right tabular-nums"
                    />
                  )}
                </Field>

                <Field label="Frequency" required>
                  {(props) => (
                    <Select
                      {...props}
                      value={frequency}
                      onChange={(e) =>
                        setFrequency(e.target.value as (typeof RENT_FREQUENCIES)[number])
                      }
                    >
                      {RENT_FREQUENCIES.map((value) => (
                        <option key={value} value={value}>
                          {perLabel(value)}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              </div>

              {rentIssue ? (
                <p role="alert" className="mt-2 text-[0.75rem] text-warn">
                  {rentIssue}
                </p>
              ) : null}

              <p className="mt-3 text-[0.75rem] text-ink-subtle">
                Leave the amount empty if it is not agreed yet — a tenancy with no schedule raises
                no charges, and the rent can be set from the tenancy afterwards. Charges are an
                accrual: raising them does not post to the ledger, receipting does.
              </p>
            </Panel>

            {/* ── Notes ────────────────────────────────────────────────────── */}
            <Panel className="p-5">
              <PanelHeader
                title="Notes"
                subtitle="Anything about this letting the next person needs to know"
                className="p-0 pb-3"
              />
              <Textarea
                aria-label="Notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Panel>
          </div>

          <div className="flex flex-col gap-4">
            {/* ── The letting ──────────────────────────────────────────────── */}
            <Panel className="p-5">
              <PanelHeader title="Tenancy" className="p-0 pb-3" />

              <div className="flex flex-col gap-3">
                <Field label="Term" required>
                  {(props) => (
                    <Select
                      {...props}
                      value={termType}
                      onChange={(e) =>
                        setTermType(e.target.value === 'fixed' ? 'fixed' : 'periodic')
                      }
                    >
                      <option value="periodic">Periodic — no agreed end date</option>
                      <option value="fixed">Fixed term</option>
                    </Select>
                  )}
                </Field>

                <Field label="Starts on" required>
                  {(props) => (
                    <Input
                      {...props}
                      type="date"
                      value={startsOn}
                      onChange={(e) => setStartsOn(e.target.value)}
                    />
                  )}
                </Field>

                {termType === 'fixed' ? (
                  <Field
                    label="Fixed term ends"
                    required
                    hint="What was signed — not the day the tenancy actually ends"
                  >
                    {(props) => (
                      <Input
                        {...props}
                        type="date"
                        value={fixedTermEndsOn}
                        onChange={(e) => setFixedTermEndsOn(e.target.value)}
                      />
                    )}
                  </Field>
                ) : null}
              </div>

              <p className="mt-3 border-t border-rule pt-3 text-[0.75rem] text-ink-subtle">
                Its ledger account is opened in the same transaction — a letting whose rent has
                nowhere to sit is a letting that cannot be receipted.
              </p>
            </Panel>

            {/* ── People ───────────────────────────────────────────────────── */}
            {/*
             * Present as an explanation rather than as fields, because it genuinely cannot be
             * done here: a tenant is a ROLE on a person record, and one human stays one record
             * however many tenancies they have been part of. Typing names into this form would
             * create a second Aroha Whitiora.
             */}
            <Panel className="p-5">
              <PanelHeader title="People" className="p-0 pb-3" />
              <p className="text-[0.8125rem] text-ink-muted">
                Attached after this is saved, from the tenancy — as a role on a person record, so
                one human stays one record however many tenancies they have been part of.
              </p>
            </Panel>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button type="submit" size="lg" disabled={!ready || create.isPending}>
            {create.isPending ? 'Starting' : 'Start tenancy'}
          </Button>
          <Link to="/tenancies">
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Link>
        </div>
      </form>
    </div>
  );
}
